import React, { useEffect, useState, useRef } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import styled from "styled-components";
import { Modal } from "semantic-ui-react";
import CloseImg from "assets/images/nav/close.svg";
import PlusImg from "assets/images/nav/plus.svg";
import CheckImg from "assets/images/nav/check.svg";
import { SET_NEW_FILTERS } from "constants/actions";
import ImageButton from "new_components/molecules/ImageButton";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Tooltip from "new_components/molecules/Tooltip";

// Design tokens
const TOKENS = {
  colors: {
    primary500: '#2D70E2',
    primary100: '#EBF1FD',
    neutral200: '#F2F2F2',
    neutral500: '#DFDFDF',
    neutral600: '#666D79',
    text: '#2A3039',
    danger500: '#F15062',
    border: '#DFDFDF',
    divider: '#E5E5E5',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
  },
  borderRadius: '8px',
};

const Filters = ({ filters, enableFilters }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );
  const { core_data } = useSelector(
    (state) => ({ core_data: state.audit?.core_data }),
    shallowEqual
  );

  const categoryRefs = useRef({});
  const [showOverflowModal, setShowOverflowModal] = useState(false);
  const [visibleTags, setVisibleTags] = useState([]);
  const [overflowTags, setOverflowTags] = useState([]);
  const containerRef = useRef(null);
  const mainContentRef = useRef(null);

  // Build refs for each category
  useEffect(() => {
    const refs = {};
    core_data?.categories?.[0]?.categories?.forEach((cat) => {
      refs[cat.id] = React.createRef();
    });
    categoryRefs.current = refs;
  }, [core_data?.categories]);

  // Add this useEffect to handle tag visibility
  useEffect(() => {
    if (!containerRef.current) return;

    const updateVisibleTags = () => {
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      const tags = container.querySelectorAll('[data-tag]');
      let currentWidth = 0;
      const visible = [];
      const overflow = [];

      tags.forEach((tag, index) => {
        const tagWidth = tag.offsetWidth;
        if (currentWidth + tagWidth + 100 <= containerWidth) { // 100px buffer for the "show more" card
          currentWidth += tagWidth;
          visible.push(selectedFilters[index]);
        } else {
          overflow.push(selectedFilters[index]);
        }
      });

      setVisibleTags(visible);
      setOverflowTags(overflow);
    };

    updateVisibleTags();
    window.addEventListener('resize', updateVisibleTags);
    return () => window.removeEventListener('resize', updateVisibleTags);
  }, [selectedFilters]);

  const handleSelect = (item, parent) => {
    if (!enableFilters) return;

    const selectedItem = {
      ...item,
      parent: parent?.id || parent?.name,
      id: item.id || item.key || item.value || item.name,
    };
    const exists = selectedFilters.some(
      (i) => i.id === selectedItem.id && i.parent === selectedItem.parent
    );
    const updated = exists
      ? selectedFilters.filter(
        (i) => !(i.id === selectedItem.id && i.parent === selectedItem.parent)
      )
      : [...selectedFilters, selectedItem];

    dispatch({ type: SET_NEW_FILTERS, payload: updated });
  };

  const clearAll = () => {
    if (!enableFilters) return;
    dispatch({ type: SET_NEW_FILTERS, payload: [] });
    setOpen(false);
  };

  const setSelectAll = () => {
    if (!enableFilters) return;
    dispatch({
      type: SET_NEW_FILTERS,
      payload: [{ name: 'Entire organization', parent: null, id: null }],
    });
    setOpen(false);
  };

  const handleCategoryClick = (categoryId) => {
    if (!enableFilters) return;
    const ref = categoryRefs.current[categoryId]?.current;
    if (ref && mainContentRef.current) {
      const mainContent = mainContentRef.current;
      const categoryTop = ref.offsetTop - 130;
      mainContent.scrollTo({
        top: categoryTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Wrapper>
      <SelectedBar>
        <LeftSection>
          <Tooltip
            tooltipText={!enableFilters ? "To add filters, please select an engagement factor above" : ""}
            showTip={!enableFilters}
          >
            <ActionButton
              onClick={() => enableFilters && setOpen(true)}
              disabled={!enableFilters}
            >
              <Icon src={PlusImg} alt="Add" />
              {selectedFilters.length > 0
                ? `Filters (${selectedFilters.length})`
                : 'Add filters'}
            </ActionButton>
          </Tooltip>
        </LeftSection>

        <MiddleSection>
          {selectedFilters.map((item, i) => (
            <Tooltip
              key={i}
              tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
              showTip={!enableFilters}
            >
              <Tag>
                {item.name}
                <ImageButton
                  textStyle
                  icon={ICON_SET.clear}
                  onClick={() => enableFilters && handleSelect(item, { id: item.parent })}
                  disabled={!enableFilters}
                />
              </Tag>
            </Tooltip>
          ))}
        </MiddleSection>

        {selectedFilters.length > 0 && (
          <RightSection>
            <Tooltip
              tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
              showTip={!enableFilters}
            >
              <ClearText
                onClick={clearAll}
                disabled={!enableFilters}
              >
                Clear all
              </ClearText>
            </Tooltip>
          </RightSection>
        )}
      </SelectedBar>

      <Modal open={open} onClose={() => setOpen(false)} closeIcon={false}>
        <Modal.Header>
          <Header>
            <Title>Filters</Title>
            <CloseButton onClick={() => setOpen(false)}>
              <Icon color={TOKENS.colors.neutral600} src={CloseImg} alt="Close" />
            </CloseButton>
          </Header>
        </Modal.Header>

        <Modal.Content>
          <SearchWrapper>
            <Tooltip
              tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
              showTip={!enableFilters}
            >
              <SearchInput
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!enableFilters}
              />
            </Tooltip>
          </SearchWrapper>

          <Content>
            <Sidebar>
              <CategoryHeading>Filter by category</CategoryHeading>
              {core_data?.categories?.[0]?.categories?.map((f) => (
                <Tooltip
                  key={f.id}
                  tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
                  showTip={!enableFilters}
                >
                  <CategoryTitle
                    onClick={() => handleCategoryClick(f.id)}
                    disabled={!enableFilters}
                  >
                    {f.name}
                  </CategoryTitle>
                </Tooltip>
              ))}
            </Sidebar>

            <Divider />

            <Main ref={mainContentRef}>
              {core_data?.categories?.[0]?.categories?.map((cat) => (
                <Section key={cat.id} ref={categoryRefs.current[cat.id]}>
                  <SectionTitle>{cat.name}</SectionTitle>
                  <Options>
                    {cat.options
                      .filter((o) =>
                        o.name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((opt) => {
                        const isSelected = selectedFilters.some(
                          (i) => i.id === opt.id && i.parent === cat.id
                        );
                        return (
                          <Tooltip
                            key={opt.id}
                            tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
                            showTip={!enableFilters}
                          >
                            <Option
                              selected={isSelected}
                              onClick={() => handleSelect(opt, cat)}
                              disabled={!enableFilters}
                            >
                              {isSelected && (
                                <Icon src={CheckImg} alt="Selected" />
                              )}
                              {opt.name}
                            </Option>
                          </Tooltip>
                        );
                      })}
                  </Options>
                </Section>
              ))}
            </Main>
          </Content>
        </Modal.Content>

        <Modal.Actions>
          <Footer>
            <Tooltip
              tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
              showTip={!enableFilters}
            >
              <SecondaryButton
                onClick={setSelectAll}
                disabled={!enableFilters}
              >
                Select all
              </SecondaryButton>
            </Tooltip>
            <Tooltip
              tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
              showTip={!enableFilters}
            >
              <PrimaryButton
                onClick={() => setOpen(false)}
                disabled={!enableFilters}
              >
                Add ({selectedFilters.length})
              </PrimaryButton>
            </Tooltip>
          </Footer>
        </Modal.Actions>
      </Modal>

      {/* New overflow modal */}
      <OverflowModal
        open={showOverflowModal}
        onClose={() => setShowOverflowModal(false)}
        size="tiny"
      >
        <Modal.Header>
          <Header>
            <Title>Additional Filters</Title>
            <CloseButton onClick={() => setShowOverflowModal(false)}>
              <Icon color={TOKENS.colors.neutral600} src={CloseImg} alt="Close" />
            </CloseButton>
          </Header>
        </Modal.Header>
        <Modal.Content>
          <OverflowList>
            {overflowTags.map((item, i) => (
              <Tooltip
                key={i}
                tooltipText={!enableFilters ? "Please select an Engagement Survey first" : ""}
                showTip={!enableFilters}
              >
                <OverflowTag>
                  {item.name}
                  <ImageButton
                    textStyle
                    icon={ICON_SET.clear}
                    onClick={() => enableFilters && handleSelect(item, { id: item.parent })}
                    disabled={!enableFilters}
                  />
                </OverflowTag>
              </Tooltip>
            ))}
          </OverflowList>
        </Modal.Content>
      </OverflowModal>
    </Wrapper>
  );
};

export default Filters;

// Styled components
const Wrapper = styled.div`
  width: 100%;
`;

const SelectedBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOKENS.spacing.md};
  // margin-bottom: ${TOKENS.spacing.sm};
  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const MiddleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOKENS.spacing.sm};
  flex: 1;
  min-height: 50px;
  flex-wrap: wrap;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOKENS.spacing.sm};
  background: white;
  color: ${TOKENS.colors.text};
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.sm} ${TOKENS.spacing.sm} ${TOKENS.spacing.md};
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${TOKENS.colors.border};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${TOKENS.spacing.xs};
  background: transparent;
  border: 1px solid ${TOKENS.colors.primary500};
  color: ${TOKENS.colors.primary500};
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.md};
  font-size: 14px;
  font-weight: 600;
  border-radius: ${TOKENS.borderRadius};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const ClearText = styled.span`
  color: ${TOKENS.colors.text};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: bold;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
`;

const SearchWrapper = styled.div`
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${TOKENS.spacing.sm};
  font-size: 14px;
  border: 1px solid ${TOKENS.colors.border};
  border-radius: ${TOKENS.borderRadius};
  color: ${TOKENS.colors.text};
  background: ${props => props.disabled ? TOKENS.colors.neutral200 : '#fff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  &::placeholder {
    color: ${TOKENS.colors.neutral600};
  }
`;

const Content = styled.div`
  display: flex;
  height: 400px;
`;

const Sidebar = styled.div`
  width: 240px;
  padding: ${TOKENS.spacing.lg};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.spacing.sm};
  border-right: 1px solid ${TOKENS.colors.divider};
`;

const Divider = styled.div`
  width: 1px;
  background: ${TOKENS.colors.divider};
`;

const Main = styled.div`
  flex: 1;
  padding: ${TOKENS.spacing.lg};
  overflow-y: auto;
`;

const CategoryHeading = styled.div`
  font-family: Raleway;
  font-size: 14px;
  color: ${TOKENS.colors.text};
  margin-bottom: ${TOKENS.spacing.sm};
  font-weight: 600;
`;

const CategoryTitle = styled.div`
  font-family: Raleway;
  font-size: 14px;
  color: ${TOKENS.colors.text};
  margin-bottom: ${TOKENS.spacing.sm};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const Section = styled.div`
  margin-bottom: ${TOKENS.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: ${TOKENS.spacing.sm};
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${TOKENS.spacing.sm};
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOKENS.spacing.xs};
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.md};
  background: ${(p) => (p.selected ? TOKENS.colors.primary100 : '#fff')};
  color: ${(p) => (p.selected ? TOKENS.colors.primary500 : TOKENS.colors.text)};
  border-radius: ${TOKENS.borderRadius};
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const PrimaryButton = styled.button`
  background: ${props => props.disabled ? TOKENS.colors.neutral500 : TOKENS.colors.primary500};
  color: #fff;
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.lg};
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: ${TOKENS.borderRadius};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const SecondaryButton = styled.button`
  background: #fff;
  color: ${props => props.disabled ? TOKENS.colors.neutral600 : TOKENS.colors.primary500};
  padding: ${TOKENS.spacing.sm} ${TOKENS.spacing.lg};
  font-size: 14px;
  font-weight: 600;
  border: 1px solid ${props => props.disabled ? TOKENS.colors.neutral500 : TOKENS.colors.primary500};
  border-radius: ${TOKENS.borderRadius};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
`;

const OverflowModal = styled(Modal)`
  .content {
    max-height: 400px;
    overflow-y: auto;
  }
`;

const OverflowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.spacing.sm};
  padding: ${TOKENS.spacing.md};
`;

const OverflowTag = styled(Tag)`
  width: 100%;
  justify-content: space-between;
`;
